import { useContext, useEffect, useRef, useState } from "react";
import NumberBox from "../../components/ui/number-box/numberbox";
import * as api from "../../utils/Api.ts";
import { InitDataContext } from "../../context/InitDataContext";
import Radar from "../../components/widgets/graphs/Radar";
import "../../utils/main.css";
import VerticalBar from "../../components/widgets/graphs/Bars.tsx";
import TableRecentsStatistics from "../../components/widgets/TableRecentItems.tsx";
import { Toast } from "primereact/toast";
import {
  CENTRO_COSTO_ADQUISICION,
  CENTRO_COSTO_GENERAL,
  FORMAT_THOUSANDS,
  showMsg,
} from "../../utils/Constants.tsx";

const labelsMonths = [
  "Enero",
  "Febrero",
  "Marzo",
  "Abril",
  "Mayo",
  "Junio",
  "Julio",
  "Agosto",
  "Septiembre",
  "Octubre",
  "Noviembre",
  "Diciembre",
];
const labelsYear = [new Date().getFullYear()];

export default function IndexSolicitudesCompra() {
  interface SolicitudeStatistics {
    total: number;
    pendientes: number;
    anuladas: number;
    rechazadas_parcial: number;
    rechazadas: number;
    aprobadas_parcial: number;
    aprobadas: number;
    confirmadas_parcial: number;
    confirmadas: number;
    emitidas: number;
  }

  const [isLoadingMonth, setIsLoadingMonth] = useState(true);
  const [isLoadingYear, setIsLoadingYear] = useState(true);
  const [isLoadingPptoData, setIsLoadingPptoData] = useState(true);
  const solicitudeStatisticsMonth = useRef<SolicitudeStatistics | null>();
  const solicitudeStatisticsYear = useRef<SolicitudeStatistics | null>();

  const pptoDataStatisticsMonth = useRef<Record<number, any> | null>();
  const pptoDataStatisticsYear = useRef<Record<number, any> | null>();
  const solicitudePptoStatisticsMonth = useRef();
  const solicitudePptoStatisticsYear = useRef();
  const solicitudeAllPptoStatisticsMonths = useRef<Record<string, any> | null>(
    null,
  );
  const jsonArrayGraphPptoData = useRef<number[] | undefined>();
  const jsonArrayGraphSolicitudePptoYear = useRef<number[] | undefined>();
  const jsonArrayGraphPptoDataBase = useRef<number[] | undefined>();
  const jsonArrayGraphPptoDataBaseYear = useRef<number[] | undefined>();
  const dataRecentSolicitudes = useRef<any[]>([]);
  const dataStatisticsSolicitudesCentros = useRef<any[]>([]);
  const [isLoadingFooterDataRecents, setIsLoadingFooterDataRecents] =
    useState(true);
  const [isLoadingFooterDataCentros, setIsLoadingFooterDataCentros] =
    useState(true);

  const { initData } = useContext(InitDataContext);
  const CENTRO_COSTO_USUARIO = initData?.centro_costo.toUpperCase();
  const CENTRO_COSTO_VISTA_USUARIO =
    initData?.centros_costo_vista_nombre.toUpperCase();
  const toast = useRef(null);
  const today = new Date();
  const currentMonth = today.getMonth() + 1;
  const currentYear = today.getFullYear();
  const formatMonthYearTwoDigit =
    currentMonth < 10
      ? `0${currentMonth}-${currentYear}`
      : `${currentMonth}-${currentYear}`;
  const formatYear = `-${currentYear}`;

  const rolesDeconstructedVal = initData.roles.some(
    (role: { codigo: string }) =>
      role.codigo === "APRSOC" || role.codigo === "CONFSOC",
  );

  const subroganteVal =
    !!initData.mi_subrogancia_soc &&
    initData.mi_subrogancia_soc.trim().length > 0
      ? true
      : false;

  useEffect(() => {
    const fetchSolicitudesStatistics = async () => {
      try {
        const responseGetSolicitudeStatisticsMonth =
          await api.GET_SOLICITUDES_STATISTICS_MONTH.getSolicitudeStatisticsMonth(
            initData.id,
            initData.id_centro_costo,
            initData.centros_costo_vista,
            rolesDeconstructedVal,
            subroganteVal,
          );
        if (responseGetSolicitudeStatisticsMonth !== null) {
          solicitudeStatisticsMonth.current =
            responseGetSolicitudeStatisticsMonth;
          setIsLoadingMonth(false);
        } else {
          showMsg(
            toast,
            "error",
            "Error",
            "Error capturando datos mensuales de las soc por centro costo y usuario",
          );
        }

        //ESTADISTICA ANUAL
        const responseGetSolicitudeStatistics =
          await api.GET_SOLICITUDES_STATISTICS_YEAR.getSolicitudeStatisticsYear(
            initData.id,
            initData.id_centro_costo,
            initData.centros_costo_vista,
            rolesDeconstructedVal,
            subroganteVal,
          );
        if (responseGetSolicitudeStatistics !== null) {
          solicitudeStatisticsYear.current = responseGetSolicitudeStatistics;
          setIsLoadingYear(false);
        } else {
          showMsg(
            toast,
            "error",
            "Error",
            "Error capturando datos anuales de las soc por centro costo y usuario",
          );
        }
      } catch (error) {
        console.error("Error fetching solicitude statistics:", error);
      }
    };

    const fetchRecentSolicitudes = async () => {
      try {
        const responseGetRecentSolicitudes =
          await api.GET_STATISTICS_RECENT_SOLICITUDES.getStatisticsRecentSolicitudes(
            initData.id,
          );
        if (responseGetRecentSolicitudes !== null) {
          dataRecentSolicitudes.current = responseGetRecentSolicitudes;
          setIsLoadingFooterDataRecents(false);
        } else {
          showMsg(
            toast,
            "error",
            "Error",
            "Error capturando datos de las solicitudes más recientes",
          );
        }
      } catch (error) {
        console.error("Error fetching recent solicitudes:", error);
      }
    };

    const fetchStatisticsByCentros = async () => {
      try {
        const responseGetStatisticsCentros =
          await api.GET_STATISTICS_SOLICITUDES_COST_CENTER.getStatisticsSolicitudesCostCenter(
            initData.id,
          );
        if (responseGetStatisticsCentros !== null) {
          dataStatisticsSolicitudesCentros.current =
            responseGetStatisticsCentros;
          setIsLoadingFooterDataCentros(false);
        } else {
          showMsg(
            toast,
            "error",
            "Error",
            "Error capturando datos de las solicitudes de los centros más demandantes",
          );
        }
      } catch (error) {
        console.error(
          "Error fetching recent solicitudes from demanding centers:",
          error,
        );
      }
    };

    const fetchPptoStatistics = async () => {
      try {
        //ESTADISTICA MENSUAL
        const responseGetPptoStatisticsMonth =
          await api.GET_PPTO_STATISTICS_MONTH.getPptoStatisticsMonth(
            initData.id,
            initData.id_centro_costo,
            initData.centros_costo_vista,
            rolesDeconstructedVal,
            subroganteVal,
          );
        if (responseGetPptoStatisticsMonth !== null) {
          pptoDataStatisticsMonth.current = responseGetPptoStatisticsMonth;
        } else {
          showMsg(
            toast,
            "error",
            "Error",
            "Error capturando datos mensuales de presupuesto",
          );
        }

        const responseGetSolicitudePptoStatisticsMonth =
          await api.GET_SOLICITUDE_REF_PPTO_STATISTICS_MONTH.getSolicitudeRefPptoStatisticsMonth(
            initData.id,
            initData.id_centro_costo,
          );
        if (responseGetSolicitudePptoStatisticsMonth !== null) {
          solicitudePptoStatisticsMonth.current =
            responseGetSolicitudePptoStatisticsMonth;
        } else {
          showMsg(
            toast,
            "error",
            "Error",
            "Error capturando datos mensuales de suma solicitudes ref presupuesto",
          );
        }

        const responseGetAllPptoSolicitudeMonths =
          await api.GET_ALL_PPTO_SOLICITUDE_MONTHS.getAllPptoSolicitudeMonths(
            initData.id,
            initData.id_centro_costo,
            initData.centros_costo_vista,
            rolesDeconstructedVal,
            subroganteVal,
          );
        if (responseGetAllPptoSolicitudeMonths !== null) {
          solicitudeAllPptoStatisticsMonths.current =
            responseGetAllPptoSolicitudeMonths;

          //CREAMOS EL ARREGLO PARA LOS GRAFICOS DE PRESUPUESTO POR SOLICITUDES REFERENCIALES
          const jsonArrayGraphPpto = [];
          const jsonArrayGraphPptoYear = [];
          const obj = solicitudeAllPptoStatisticsMonths.current || {};
          for (const key in obj) {
            let value = 0;
            let valueYearly = 0;
            if (obj[key] !== null) {
              value = parseFloat(obj[key]);
            }
            if (key === "total" || key === "total_ano_pasado") {
              valueYearly = obj[key];
              jsonArrayGraphPptoYear.push(valueYearly);
            } else {
              jsonArrayGraphPpto.push(value);
            }
          }
          jsonArrayGraphPptoData.current = jsonArrayGraphPpto;
          jsonArrayGraphSolicitudePptoYear.current = jsonArrayGraphPptoYear;
        } else {
          showMsg(
            toast,
            "error",
            "Error",
            "Error capturando datos mensuales de suma solicitudes ref presupuesto",
          );
        }

        //ESTADISTICA ANUAL
        const responseGetPptoStatisticsYear =
          await api.GET_PPTO_STATISTICS_YEAR.getPptoStatisticsYear(
            initData.id,
            initData.id_centro_costo,
            initData.centros_costo_vista,
            rolesDeconstructedVal,
            subroganteVal,
          );
        if (responseGetPptoStatisticsYear !== null) {
          pptoDataStatisticsYear.current = responseGetPptoStatisticsYear;

          //CREAMOS EL ARREGLO PARA LOS GRAFICOS DE PRESUPUESTO
          const jsonArrayGraphPptoBase = [];
          const jsonArrayGraphPptoBaseYear = [];
          const obj = pptoDataStatisticsYear.current || {};
          for (const key in obj) {
            let value = 0;
            let valueYearly = 0;
            if (obj[key] !== null) {
              value = obj[key];
            }
            if (key === "total" || key === "ppto_ano_anterior") {
              valueYearly = obj[key];
              jsonArrayGraphPptoBaseYear.push(valueYearly);
            } else {
              jsonArrayGraphPptoBase.push(value);
            }
          }
          //ESTADISTICA DE PRESUPUESTO AÑO EN CURSO POR MES
          jsonArrayGraphPptoDataBase.current = jsonArrayGraphPptoBase;
          //ESTADISTICA PARA GRAFICO DE PRESUPUESTO TOTAL AÑO ANTERIOR VS AÑO EN CURSO
          jsonArrayGraphPptoDataBaseYear.current = jsonArrayGraphPptoBaseYear;
        } else {
          showMsg(
            toast,
            "error",
            "Error",
            "Error capturando datos anuales de presupuesto",
          );
        }

        const responseGetSolicitudePptoStatisticsYear =
          await api.GET_SOLICITUDE_REF_PPTO_STATISTICS_YEAR.getSolicitudeRefPptoStatisticsYear(
            initData.id,
            initData.id_centro_costo,
          );

        if (responseGetSolicitudePptoStatisticsYear !== null) {
          solicitudePptoStatisticsYear.current =
            responseGetSolicitudePptoStatisticsYear;

          setIsLoadingPptoData(false);
        } else {
          showMsg(
            toast,
            "error",
            "Error",
            "Error capturando datos anuales de suma solicitudes ref presupuesto",
          );
        }
      } catch (error) {
        console.error("Error fetching budget statistics:", error);
      }
    };
    fetchSolicitudesStatistics();
    fetchPptoStatistics();
    fetchRecentSolicitudes();
    fetchStatisticsByCentros();
  }, []);

  const createUrlWithFilters = (
    socest_nombre: string,
    soc_dt_cre: string,
    cc_nombre: string,
  ) => {
    let url = `solicitudes-compra/mantencion?socest_nombre=${socest_nombre}&soc_dt_cre=${soc_dt_cre}`;
    return url;
  };

  const renderSolicitudeStatesMonth = () => {
    if (solicitudeStatisticsMonth.current) {
      return (
        <div className="rowContainer">
          <NumberBox
            title="Totales"
            value={
              solicitudeStatisticsMonth.current
                ? FORMAT_THOUSANDS(solicitudeStatisticsMonth.current.total)
                : 0
            }
            backColor={"--primary-app-color"}
            url={`solicitudes-compra/mantencion?socest_nombre=&soc_dt_cre=-${formatMonthYearTwoDigit}`}
          />
          <NumberBox
            title="Pendientes"
            value={
              solicitudeStatisticsMonth.current
                ? solicitudeStatisticsMonth.current.pendientes
                : 0
            }
            backColor={"--color-pendiente"}
            url={`solicitudes-compra/mantencion?socest_nombre=Pendiente&soc_dt_cre=-${formatMonthYearTwoDigit}`}
          />
          <NumberBox
            title="Anuladas"
            value={
              solicitudeStatisticsMonth.current
                ? solicitudeStatisticsMonth.current.anuladas
                : 0
            }
            backColor={"--color-anuladas"}
            url={`solicitudes-compra/mantencion?socest_nombre=Anulada&soc_dt_cre=-${formatMonthYearTwoDigit}`}
          />
          {/*<NumberBox
            title="Rechazadas parcial"
            value={
              solicitudeStatisticsMonth.current
                ? solicitudeStatisticsMonth.current.rechazadas_parcial
                : 0
            }
            backColor={"--color-rechazadas-parcial"}
            url={`solicitudes-compra/mantencion?socest_nombre=Rechazada parcialmente&soc_dt_cre=-${formatMonthYearTwoDigit}`}
          />*/}
          <NumberBox
            title="Rechazadas"
            value={
              solicitudeStatisticsMonth.current
                ? solicitudeStatisticsMonth.current.rechazadas
                : 0
            }
            backColor={"--color-rechazadas"}
            url={`solicitudes-compra/mantencion?socest_nombre=Rechazada&soc_dt_cre=-${formatMonthYearTwoDigit}`}
          />
          {/* <NumberBox
            title="Aprobadas parcial"
            value={
              solicitudeStatisticsMonth.current
                ? solicitudeStatisticsMonth.current.aprobadas_parcial
                : 0
            }
            backColor={"--color-aprobadas-parcial"}
            url={`solicitudes-compra/mantencion?socest_nombre=Aprobada parcialmente&soc_dt_cre=-${formatMonthYearTwoDigit}`}
          />*/}
          <NumberBox
            title="Aprobadas"
            value={
              solicitudeStatisticsMonth.current
                ? solicitudeStatisticsMonth.current.aprobadas
                : 0
            }
            backColor={"--color-aprobadas"}
            url={`solicitudes-compra/mantencion?socest_nombre=Aprobada&soc_dt_cre=-${formatMonthYearTwoDigit}`}
          />
          {/* <NumberBox
            title="Confirmadas parcial"
            value={
              solicitudeStatisticsMonth.current
                ? solicitudeStatisticsMonth.current.confirmadas_parcial
                : 0
            }
            backColor={"--color-confirmadas-parcial"}
            url={`solicitudes-compra/mantencion?socest_nombre=Confirmada parcialmente&soc_dt_cre=-${formatMonthYearTwoDigit}`}
          />*/}
          <NumberBox
            title="Confirmadas"
            value={
              solicitudeStatisticsMonth.current
                ? FORMAT_THOUSANDS(
                    solicitudeStatisticsMonth.current.confirmadas,
                  )
                : 0
            }
            backColor={"--color-confirmadas"}
            url={`solicitudes-compra/mantencion?socest_nombre=Confirmada&soc_dt_cre=-${formatMonthYearTwoDigit}`}
          />
          <NumberBox
            title="Emitidas"
            value={
              solicitudeStatisticsMonth.current
                ? FORMAT_THOUSANDS(solicitudeStatisticsMonth.current.emitidas)
                : 0
            }
            backColor={"--color-emitidas"}
            url={`solicitudes-compra/mantencion?socest_nombre=Emitida&soc_dt_cre=-${formatMonthYearTwoDigit}`}
          />
        </div>
      );
    }
  };

  const renderSolicitudeStatesYear = () => {
    if (solicitudeStatisticsYear.current) {
      return (
        <div className="rowContainer">
          <NumberBox
            title="Totales"
            value={
              solicitudeStatisticsYear.current
                ? FORMAT_THOUSANDS(solicitudeStatisticsYear.current.total)
                : 0
            }
            backColor={"--primary-app-color"}
            url={
              "solicitudes-compra/mantencion?socest_nombre=&soc_dt_cre=" +
              formatYear +
              "&cc_nombre=" +
              initData.centro_costo
            }
          />
          <NumberBox
            title="Pendientes"
            value={
              solicitudeStatisticsYear.current
                ? solicitudeStatisticsYear.current.pendientes
                : 0
            }
            backColor={"--color-pendiente"}
            url={
              "solicitudes-compra/mantencion?socest_nombre=Pendiente&soc_dt_cre=" +
              formatYear +
              "&cc_nombre=" +
              initData.centro_costo
            }
          />
          <NumberBox
            title="Anuladas"
            value={
              solicitudeStatisticsYear.current
                ? solicitudeStatisticsYear.current.anuladas
                : 0
            }
            backColor={"--color-anuladas"}
            url={
              "solicitudes-compra/mantencion?socest_nombre=Anulada&soc_dt_cre=" +
              formatYear +
              "&cc_nombre=" +
              initData.centro_costo
            }
          />
          {/* <NumberBox
            title="Rechazadas parcial"
            value={
              solicitudeStatisticsYear.current
                ? solicitudeStatisticsYear.current.rechazadas_parcial
                : 0
            }
            backColor={"--color-rechazadas-parcial"}
            url={
              "solicitudes-compra/mantencion?socest_nombre=Rechazada parcialmente&soc_dt_cre=" +
              formatYear +
              "&cc_nombre=" +
              initData.centro_costo
            }
          />*/}
          <NumberBox
            title="Rechazadas"
            value={
              solicitudeStatisticsYear.current
                ? solicitudeStatisticsYear.current.rechazadas
                : 0
            }
            backColor={"--color-rechazadas"}
            url={
              "solicitudes-compra/mantencion?socest_nombre=Rechazada&soc_dt_cre=" +
              formatYear +
              "&cc_nombre=" +
              initData.centro_costo
            }
          />
          {/* <NumberBox
            title="Aprobadas parcial"
            value={
              solicitudeStatisticsYear.current
                ? solicitudeStatisticsYear.current.aprobadas_parcial
                : 0
            }
            backColor={"--color-aprobadas-parcial"}
            url={
              "solicitudes-compra/mantencion?socest_nombre=Aprobada parcialmente&soc_dt_cre=" +
              formatYear +
              "&cc_nombre=" +
              initData.centro_costo
            }
          />*/}
          <NumberBox
            title="Aprobadas"
            value={
              solicitudeStatisticsYear.current
                ? solicitudeStatisticsYear.current.aprobadas
                : 0
            }
            backColor={"--color-aprobadas"}
            url={
              "solicitudes-compra/mantencion?socest_nombre=Aprobada&soc_dt_cre=" +
              formatYear +
              "&cc_nombre=" +
              initData.centro_costo
            }
          />
          {/* <NumberBox
            title="Confirmadas parcial"
            value={
              solicitudeStatisticsYear.current
                ? solicitudeStatisticsYear.current.confirmadas_parcial
                : 0
            }
            backColor={"--color-confirmadas-parcial"}
            url={
              "solicitudes-compra/mantencion?socest_nombre=Confirmada parcialmente&soc_dt_cre=" +
              formatYear +
              "&cc_nombre=" +
              initData.centro_costo
            }
          />*/}
          <NumberBox
            title="Confirmadas"
            value={
              solicitudeStatisticsYear.current
                ? FORMAT_THOUSANDS(solicitudeStatisticsYear.current.confirmadas)
                : 0
            }
            backColor={"--color-confirmadas"}
            url={
              "solicitudes-compra/mantencion?socest_nombre=Confirmada&soc_dt_cre=" +
              formatYear +
              "&cc_nombre=" +
              initData.centro_costo
            }
          />
          <NumberBox
            title="Emitidas"
            value={
              solicitudeStatisticsYear.current
                ? FORMAT_THOUSANDS(solicitudeStatisticsYear.current.emitidas)
                : 0
            }
            backColor={"--color-emitidas"}
            url={
              "solicitudes-compra/mantencion?socest_nombre=Emitida&soc_dt_cre=" +
              formatYear +
              "&cc_nombre=" +
              initData.centro_costo
            }
          />
        </div>
      );
    }
  };

  const renderPptoStatisticsMonth = () => {
    if (
      jsonArrayGraphPptoDataBase.current &&
      jsonArrayGraphPptoData.current &&
      solicitudePptoStatisticsMonth.current
    ) {
      return (
        <div className="rowContainer">
          <Radar
            data1={jsonArrayGraphPptoDataBase.current}
            data2={jsonArrayGraphPptoData.current}
            labels={labelsMonths}
            context="solicitude"
          />
          <VerticalBar
            data1={jsonArrayGraphPptoDataBase.current}
            data2={jsonArrayGraphPptoData.current}
            labels={labelsMonths}
            context="solicitude"
          />
        </div>
      );
    }
  };

  const renderPptoStatisticsYear = () => {
    if (
      jsonArrayGraphPptoDataBaseYear.current &&
      jsonArrayGraphSolicitudePptoYear.current &&
      solicitudePptoStatisticsYear.current
    ) {
      return (
        <div className="rowContainer">
          <Radar
            data1={jsonArrayGraphPptoDataBaseYear.current}
            data2={jsonArrayGraphSolicitudePptoYear.current}
            labels={labelsYear}
            context="solicitude"
          />
          <VerticalBar
            data1={jsonArrayGraphPptoDataBaseYear.current}
            data2={jsonArrayGraphSolicitudePptoYear.current}
            labels={labelsYear}
            context="solicitude"
          />
        </div>
      );
    }
  };

  return (
    <div>
      <Toast ref={toast} />
      <h4 className="topTitleLeft" style={{ textAlign: "left" }}>
        {initData.centros_costo_vista.includes(29) ? (
          <span>
            SOLICITUDES DEL MES EN CURSO - SALMONES DE CHILE Y PRODUCTORA S.A
          </span>
        ) : initData.roles.some(
            (role: { codigo: string }) =>
              role.codigo === "APRSOC" || role.codigo === "CONFSOC",
          ) == true && subroganteVal == true ? (
          <span>
            SOLICITUDES DEL MES EN CURSO - ASIGNADAS A {initData.nombres}
            {initData.apellido_paterno} {initData.apellido_materno}
          </span>
        ) : (
          <span>
            SOLICITUDES DEL MES EN CURSO - {CENTRO_COSTO_VISTA_USUARIO}
          </span>
        )}
      </h4>
      {isLoadingMonth ? null : renderSolicitudeStatesMonth()}
      {isLoadingMonth && isLoadingPptoData ? null : renderPptoStatisticsMonth()}
      <h4 className="topTitleLeft" style={{ textAlign: "left" }}>
        {initData.centros_costo_vista.includes(29) ? (
          <span>
            SOLICITUDES DEL AÑO EN CURSO - SALMONES DE CHILE Y PRODUCTORA S.A
          </span>
        ) : initData.roles.some(
            (role: { codigo: string }) =>
              role.codigo === "APRSOC" || role.codigo === "CONFSOC",
          ) == true && subroganteVal == true ? (
          <span>
            SOLICITUDES DEL AÑO EN CURSO - ASIGNADAS A {initData.nombres}
            {initData.apellido_paterno} {initData.apellido_materno}
          </span>
        ) : (
          <span>
            SOLICITUDES DEL AÑO EN CURSO - {CENTRO_COSTO_VISTA_USUARIO}
          </span>
        )}
      </h4>
      {isLoadingYear ? null : renderSolicitudeStatesYear()}
      {isLoadingYear && isLoadingPptoData ? null : renderPptoStatisticsYear()}
      {!isLoadingFooterDataRecents &&
      !isLoadingFooterDataCentros &&
      initData.centros_costo_vista.includes(29) ? (
        <div className="rowContainer">
          <div style={{ width: "60%" }}>
            <h4>SOLICITUDES RECIENTES</h4>
            <TableRecentsStatistics
              dataRender={dataRecentSolicitudes.current}
              operationType="recent-solicitudes"
            />
          </div>
          <div style={{ width: "40%" }}>
            <h4>CENTROS MÁS SOLICITANTES EN EL AÑO</h4>
            <TableRecentsStatistics
              dataRender={dataStatisticsSolicitudesCentros.current}
              operationType="statistics-centros"
            />
          </div>
        </div>
      ) : null}
    </div>
  );
}
